import React, { useState } from "react";
import { Sessions, Users } from "@app/shared/src/api/api.gen";
import { ChangeUserPassword, IChangeUserPassword, IUserUpdate, UserUpdate } from "@app/shared/src/models";
import { Btn, Modal, Segmented, Tag, TextInput, useToast } from "@app/shared/src/components_v2";
import { emptyOrganizationDraft, organizationDraftError, toOrganization } from "../../helpers/organizationDraft";
import { useOrg } from "../providers/OrgProvider";
import { useSession } from "../providers/SessionProvider";
import { OrganizationFields } from "./OrganizationFields";

/**
 * Your own account, editable without leaving the page you were on.
 *
 * Name and password are the two things the server lets a user change about themselves — the update
 * endpoint takes first and last name, and the password change requires the current one. Email is
 * the account's identity and has no self-service endpoint, so it is shown read-only rather than as
 * a field that silently does nothing.
 *
 * The Organizations tab is the membership list: every organization this account belongs to, which
 * one the console is showing, and the way to add another. Membership in someone else's
 * organization comes by invitation on the main dashboard, so what can be done here is create a
 * new one — the server makes the creator its administrator.
 */

type Tab = "profile" | "password" | "orgs";

export const ProfileDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const { user, refresh } = useSession();
    const { orgs, org, setOrgId, createOrg } = useOrg();
    const toast = useToast();
    const [tab, setTab] = useState<Tab>("profile");

    // The identity model carries a single display name; split it for the two-field update.
    const [firstName, setFirstName] = useState(() => (user?.name ?? "").split(" ")[0] ?? "");
    const [lastName, setLastName] = useState(() => (user?.name ?? "").split(" ").slice(1).join(" "));
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    // The Organizations tab flips between the list and the add form in place — a dialog over a
    // dialog is the alternative, and that reads as losing your place.
    const [addingOrg, setAddingOrg] = useState(false);
    const [orgDraft, setOrgDraft] = useState(emptyOrganizationDraft);

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    const close = () => {
        setError(undefined);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setAddingOrg(false);
        setOrgDraft(emptyOrganizationDraft());
        onClose();
    };

    const saveProfile = async () => {
        if (busy) return;
        setBusy(true);
        setError(undefined);
        try {
            await Users.updateUser(new UserUpdate({ firstName: firstName.trim(), lastName: lastName.trim() } as IUserUpdate));
            await refresh();
            toast("Profile updated");
            close();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not save your profile");
        } finally {
            setBusy(false);
        }
    };

    const savePassword = async () => {
        if (busy) return;
        if (newPassword !== confirmPassword) {
            setError("The new passwords don't match");
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            await Sessions.changePassword(
                new ChangeUserPassword({ currentPassword, newPassword, newPassword2: confirmPassword } as IChangeUserPassword),
            );
            toast("Password changed");
            close();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not change your password");
        } finally {
            setBusy(false);
        }
    };

    const saveOrg = async () => {
        if (busy) return;
        const problem = organizationDraftError(orgDraft);
        if (problem) {
            setError(problem);
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            await createOrg(toOrganization(orgDraft));
            toast(`${orgDraft.name.trim()} created — you're now looking at it`);
            setOrgDraft(emptyOrganizationDraft());
            setAddingOrg(false);
        } catch (err) {
            setError(err instanceof Error && err.message ? err.message : "Could not create the organization");
        } finally {
            setBusy(false);
        }
    };

    const footer =
        tab === "profile" ? (
            <>
                <Btn tone="quiet" onClick={close}>
                    Cancel
                </Btn>
                <Btn onClick={saveProfile} disabled={busy || !firstName.trim()}>
                    {busy ? "Saving…" : "Save profile"}
                </Btn>
            </>
        ) : tab === "password" ? (
            <>
                <Btn tone="quiet" onClick={close}>
                    Cancel
                </Btn>
                <Btn onClick={savePassword} disabled={busy || !currentPassword || !newPassword}>
                    {busy ? "Changing…" : "Change password"}
                </Btn>
            </>
        ) : addingOrg ? (
            <>
                <Btn
                    tone="quiet"
                    disabled={busy}
                    onClick={() => {
                        setAddingOrg(false);
                        setError(undefined);
                    }}
                >
                    Back
                </Btn>
                <Btn onClick={saveOrg} disabled={busy || !orgDraft.name.trim()}>
                    {busy ? "Creating…" : "Create organization"}
                </Btn>
            </>
        ) : (
            <>
                <Btn tone="quiet" onClick={close}>
                    Close
                </Btn>
                <Btn
                    onClick={() => {
                        setAddingOrg(true);
                        setError(undefined);
                    }}
                >
                    + Add organization
                </Btn>
            </>
        );

    return (
        <Modal open={open} onClose={close} title="Your account" subtitle={user?.email} footer={footer}>
            <Segmented
                full
                tone="accent"
                options={[
                    { key: "profile" as Tab, label: "Profile" },
                    { key: "password" as Tab, label: "Password" },
                    { key: "orgs" as Tab, label: "Organizations" },
                ]}
                value={tab}
                onChange={(t) => {
                    setTab(t);
                    setError(undefined);
                }}
            />

            {tab === "profile" && (
                <>
                    <div className="grid grid-cols-2 gap-3">
                        <span>
                            <label className="text-[12px] text-[var(--muted)]">First name</label>
                            <TextInput value={firstName} onChange={(e) => setFirstName(e.target.value)} className="mt-1.5" />
                        </span>
                        <span>
                            <label className="text-[12px] text-[var(--muted)]">Last name</label>
                            <TextInput value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-1.5" />
                        </span>
                    </div>
                    <span>
                        <label className="text-[12px] text-[var(--muted)]">Email</label>
                        <TextInput value={user?.email ?? ""} readOnly className="mt-1.5 opacity-60" />
                        <p className="mt-1.5 text-[11.5px] leading-relaxed text-[var(--faint)]">
                            Your email is your sign-in identity — support changes it, so a typo can't lock you out.
                        </p>
                    </span>
                </>
            )}

            {tab === "password" && (
                <>
                    <span>
                        <label className="text-[12px] text-[var(--muted)]">Current password</label>
                        <TextInput
                            type="password"
                            autoComplete="current-password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="mt-1.5"
                        />
                    </span>
                    <span>
                        <label className="text-[12px] text-[var(--muted)]">New password</label>
                        <TextInput
                            type="password"
                            autoComplete="new-password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="mt-1.5"
                        />
                    </span>
                    <span>
                        <label className="text-[12px] text-[var(--muted)]">Confirm new password</label>
                        <TextInput
                            type="password"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="mt-1.5"
                        />
                    </span>
                </>
            )}

            {tab === "orgs" && !addingOrg && (
                <>
                    {orgs.length === 0 ? (
                        <p className="text-[13px] leading-relaxed text-[var(--muted)]">You don't belong to any organization yet.</p>
                    ) : (
                        <ul className="divide-y divide-[var(--line)] rounded-[10px] border border-[var(--line)]">
                            {orgs.map((o) => {
                                const current = o.id === org?.id;
                                const where = [o.city, o.state].filter(Boolean).join(", ");
                                return (
                                    <li key={o.id} className="flex items-center gap-3 px-4 py-3">
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-[13.5px] font-medium">{o.name ?? "—"}</span>
                                            {where && <span className="block truncate text-[12px] text-[var(--muted)]">{where}</span>}
                                        </span>
                                        {current ? (
                                            <Tag tone="accent">Viewing</Tag>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setOrgId(o.id!);
                                                    toast(`Now looking at ${o.name ?? "that organization"}`);
                                                }}
                                                className="text-[12.5px] font-semibold text-[var(--accent)] hover:underline"
                                            >
                                                Switch to
                                            </button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <p className="text-[11.5px] leading-relaxed text-[var(--faint)]">
                        To join an organization that already exists, ask one of its administrators to invite {user?.email ?? "you"} from
                        the main Omnisight dashboard. Creating one here makes you its administrator.
                    </p>
                </>
            )}

            {tab === "orgs" && addingOrg && (
                <>
                    <p className="text-[13px] leading-relaxed text-[var(--muted)]">
                        A new organization, with you as its administrator. The console switches to it once it exists.
                    </p>
                    <OrganizationFields draft={orgDraft} onChange={setOrgDraft} disabled={busy} autoFocus />
                </>
            )}

            {error && <p className="text-[12.5px] text-[var(--crit)]">{error}</p>}
        </Modal>
    );
};
